import React, { useEffect, useState } from 'react';
import { Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import {
    adminSettingsService,
    type LlmCostKey,
    type LlmCostSettingRow,
    type LlmCostState,
    type LlmPriceTable,
} from '../../services/api';

/**
 * Settings: model-call cost ceilings (COST01, owner decision of 17 Oct).
 *
 * Super admin only (the parent renders this behind `settings.llm_cost`; the API enforces it). Shows month-to-date
 * spend against the monthly ceiling with the alarm, the per-run caps with their defaults (a value that differs from
 * its default is highlighted, as on Detection thresholds), and the price table with a row to price a new model.
 * Each value saves on its own, as the cloud vision switch does.
 */

const NUMERIC_KEYS: LlmCostKey[] = ['llm_monthly_ceiling_usd', 'llm_alarm_pct', 'llm_max_calls_per_run', 'llm_max_tokens_per_run'];

const usd = (v: number | null | undefined): string =>
    typeof v === 'number' && !isNaN(v) ? `USD ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : 'unknown';

const fmt = (v: unknown): string => (typeof v === 'number' ? v.toLocaleString('en-US') : String(v));

const parseValue = (key: LlmCostKey, raw: string): number | null => {
    const v = key === 'llm_monthly_ceiling_usd' ? parseFloat(raw) : Number(raw);
    if (isNaN(v)) return null;
    if (key !== 'llm_monthly_ceiling_usd' && !Number.isInteger(v)) return null;
    return v;
};

const SpendBar: React.FC<{ state: LlmCostState }> = ({ state }) => {
    const spent = state.spent_usd;
    const ceiling = state.ceiling_usd;
    const pct = spent !== null && ceiling > 0 ? (spent / ceiling) * 100 : spent !== null ? 100 : 0;
    const alarmPct = state.alarm.alarm_pct;
    const tone = pct >= 100 ? 'bg-destructive' : pct >= alarmPct ? 'bg-amber-500' : 'bg-emerald-500';
    return (
        <section className="space-y-2" data-testid="llm-cost-spend">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-foreground">Spend this month ({state.month}, UTC)</h3>
                <span className="text-sm text-foreground" data-testid="llm-cost-spent">
                    {usd(spent)} of {usd(ceiling)}
                    {spent !== null && ceiling > 0 && <span className="text-muted-foreground"> ({Math.round(pct)}%)</span>}
                </span>
            </div>
            <div
                className="h-3 w-full rounded-full bg-secondary overflow-hidden"
                role="progressbar"
                aria-label="Month-to-date model spend against the ceiling"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.min(100, Math.round(pct))}
            >
                <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} data-testid="llm-cost-bar" />
            </div>
            {state.alarm.active && (
                <p className="text-sm text-amber-600 bg-amber-500/10 rounded-md px-3 py-2" role="alert" data-testid="llm-cost-alarm">
                    {state.alarm.level !== null && state.alarm.level >= 100
                        ? 'The monthly ceiling is reached: model calls are refused and recorded as blocked until the month ends or the ceiling is raised.'
                        : `Alarm: month-to-date spend has crossed ${state.alarm.level}% of the ceiling.`}
                </p>
            )}
            {spent === null && (
                <p className="text-xs text-destructive" data-testid="llm-cost-spend-unknown">
                    Spend could not be read{state.spend_error ? ` (${state.spend_error})` : ''}. The monthly ceiling is not applied to an unknown figure; the per-run caps still are.
                </p>
            )}
            {state.spend && state.spend.unpriced_rows > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="llm-cost-unpriced">
                    {state.spend.unpriced_rows} call{state.spend.unpriced_rows === 1 ? '' : 's'} this month could not be priced
                    ({state.spend.unpriced_no_usage} without token counts, {state.spend.unpriced_no_price} with a model that has no price):
                    the figure is a lower bound. Add the model's price below.
                </p>
            )}
            {!state.enforced && (
                <p className="text-xs text-amber-600" data-testid="llm-cost-not-enforced">
                    Refusal is turned off on this deployment (LLM_BUDGET_ENFORCED): calls are counted but never refused.
                </p>
            )}
        </section>
    );
};

const NumericRow: React.FC<{
    row: LlmCostSettingRow;
    busy: boolean;
    onSave: (key: LlmCostKey, value: number) => Promise<void>;
}> = ({ row, busy, onSave }) => {
    const [draft, setDraft] = useState(String(row.value));
    useEffect(() => { setDraft(String(row.value)); }, [row.value]);
    const parsed = parseValue(row.key, draft);
    const changed = parsed !== null && parsed !== row.value;
    return (
        <tr className="border-t border-border align-top" data-testid={`llm-cost-row-${row.key}`}>
            <td className="px-3 py-2 font-medium">{row.label}</td>
            <td
                className={`px-3 py-2 ${row.is_default ? '' : 'bg-amber-500/10'}`}
                data-testid={`llm-cost-value-${row.key}`}
                data-differs={row.is_default ? 'false' : 'true'}
            >
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        step={row.key === 'llm_monthly_ceiling_usd' ? '0.01' : '1'}
                        min={0}
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        aria-label={row.label}
                        className={`w-28 rounded-md border border-border bg-background px-2 py-1 text-sm ${row.is_default ? 'text-foreground' : 'text-amber-600'}`}
                        data-testid={`llm-cost-input-${row.key}`}
                    />
                    <button
                        type="button"
                        disabled={busy || !changed}
                        onClick={() => parsed !== null && onSave(row.key, parsed)}
                        className="rounded-md border border-border px-2 py-1 text-xs font-medium disabled:opacity-40 hover:bg-secondary"
                        data-testid={`llm-cost-save-${row.key}`}
                    >
                        Save
                    </button>
                </div>
                {!row.is_default && <div className="text-xs text-amber-600 mt-0.5" data-testid={`llm-cost-differs-${row.key}`}>(differs from default)</div>}
            </td>
            <td className="px-3 py-2 text-muted-foreground">{fmt(row.default)}</td>
            <td className="px-3 py-2 text-muted-foreground">{row.unit}</td>
            <td className="px-3 py-2">{row.what}</td>
        </tr>
    );
};

const PriceTable: React.FC<{
    state: LlmCostState;
    busy: boolean;
    onSave: (table: LlmPriceTable) => Promise<void>;
}> = ({ state, busy, onSave }) => {
    const current = (state.values.llm_price_table ?? {}) as LlmPriceTable;
    const [model, setModel] = useState('');
    const [pin, setPin] = useState('');
    const [pout, setPout] = useState('');
    const vin = parseFloat(pin);
    const vout = parseFloat(pout);
    const valid = model.trim().length > 0 && !isNaN(vin) && !isNaN(vout) && vin >= 0 && vout >= 0;

    const add = async () => {
        if (!valid) return;
        await onSave({ ...current, [model.trim().toLowerCase()]: { in: vin, out: vout } });
        setModel(''); setPin(''); setPout('');
    };
    const remove = async (name: string) => {
        const next = { ...current };
        delete next[name];
        await onSave(next);
    };

    return (
        <section className="space-y-2" data-testid="llm-cost-prices">
            <h3 className="text-sm font-semibold text-foreground">Prices (USD per million tokens)</h3>
            <p className="text-xs text-muted-foreground">
                A model is priced by the longest name below that its name starts with. A price set here beats LLM_PRICE_TABLE,
                which beats the built-in list. Calls already recorded without a cost are priced from their tokens with this table.
            </p>
            <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2">Model (name prefix)</th>
                            <th className="px-3 py-2">Input</th>
                            <th className="px-3 py-2">Output</th>
                            <th className="px-3 py-2">Source</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {state.price_table.map(p => (
                            <tr key={p.model} className="border-t border-border" data-testid={`llm-price-${p.model}`}>
                                <td className="px-3 py-2 font-mono text-xs">{p.model}</td>
                                <td className="px-3 py-2">{p.in}</td>
                                <td className="px-3 py-2">{p.out}</td>
                                <td className="px-3 py-2 text-muted-foreground" data-testid={`llm-price-source-${p.model}`}>
                                    {p.source === 'setting' ? 'this setting' : p.source === 'env' ? 'LLM_PRICE_TABLE' : 'built-in list'}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {p.source === 'setting' && (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => remove(p.model)}
                                            className="text-xs text-muted-foreground hover:text-destructive disabled:opacity-40"
                                            data-testid={`llm-price-remove-${p.model}`}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        <tr className="border-t border-border bg-secondary/20">
                            <td className="px-3 py-2">
                                <input
                                    value={model}
                                    onChange={e => setModel(e.target.value)}
                                    placeholder="model name prefix"
                                    aria-label="Model name prefix"
                                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs font-mono"
                                    data-testid="llm-price-new-model"
                                />
                            </td>
                            <td className="px-3 py-2">
                                <input type="number" step="0.01" min={0} value={pin} onChange={e => setPin(e.target.value)}
                                    aria-label="Input price" className="w-20 rounded-md border border-border bg-background px-2 py-1 text-xs"
                                    data-testid="llm-price-new-in" />
                            </td>
                            <td className="px-3 py-2">
                                <input type="number" step="0.01" min={0} value={pout} onChange={e => setPout(e.target.value)}
                                    aria-label="Output price" className="w-20 rounded-md border border-border bg-background px-2 py-1 text-xs"
                                    data-testid="llm-price-new-out" />
                            </td>
                            <td className="px-3 py-2" colSpan={2}>
                                <button
                                    type="button"
                                    disabled={busy || !valid}
                                    onClick={add}
                                    className="rounded-md border border-border px-2 py-1 text-xs font-medium disabled:opacity-40 hover:bg-secondary"
                                    data-testid="llm-price-add"
                                >
                                    Add price
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export const LlmCostView: React.FC<{ state: LlmCostState; onChange: (next: LlmCostState) => void }> = ({ state, onChange }) => {
    const [busy, setBusy] = useState(false);

    const save = async (key: LlmCostKey, value: unknown) => {
        setBusy(true);
        try {
            onChange(await adminSettingsService.setLlmCost(key, value));
            toast.success('Saved. The new value applies to the next model call.');
        } catch {
            toast.error('Could not save. The value was not changed.');
        } finally {
            setBusy(false);
        }
    };

    const rows = NUMERIC_KEYS
        .map(k => state.settings.find(r => r.key === k))
        .filter((r): r is LlmCostSettingRow => !!r);

    return (
        <div className="space-y-6" data-testid="llm-cost-tab">
            <h2 className="text-lg font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" /> Model-call cost
            </h2>
            <p className="text-sm text-muted-foreground">
                Every model call is checked against these values before it leaves the server. A refused call is recorded as
                blocked with the cap that refused it, and the page that asked shows its fallback text.
            </p>
            <SpendBar state={state} />
            <section className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Ceilings</h3>
                <div className="overflow-x-auto border border-border rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Setting</th>
                                <th className="px-3 py-2">Value</th>
                                <th className="px-3 py-2">Default</th>
                                <th className="px-3 py-2">Unit</th>
                                <th className="px-3 py-2">What it does</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => <NumericRow key={r.key} row={r} busy={busy} onSave={save} />)}
                        </tbody>
                    </table>
                </div>
            </section>
            <PriceTable state={state} busy={busy} onSave={table => save('llm_price_table', table)} />
            {state.recent_runs.length > 0 && (
                <section className="space-y-1" data-testid="llm-cost-runs">
                    <h3 className="text-sm font-semibold text-foreground">Recent runs on this server</h3>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                        {state.recent_runs.map(r => (
                            <li key={r.run_id}>
                                <span className="font-mono">{r.run_id.slice(0, 8)}</span>: {r.calls} call{r.calls === 1 ? '' : 's'}, {r.tokens.toLocaleString('en-US')} tokens
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {state.not_refused && state.not_refused.length > 0 && (
                <p className="text-xs text-muted-foreground">Counted but not refused here: {state.not_refused.join('; ')}.</p>
            )}
        </div>
    );
};

export const LlmCostTab: React.FC = () => {
    const [state, setState] = useState<LlmCostState | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        adminSettingsService.getLlmCost()
            .then(s => { if (!cancelled) setState(s); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, []);

    if (error) return <p className="text-sm text-muted-foreground" role="alert">The cost settings could not be loaded.</p>;
    if (!state) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>;
    return <LlmCostView state={state} onChange={setState} />;
};

export default LlmCostTab;
